import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { ListItem } from '@mrrmlab/shared-types';
import type {
  DebugParseImageResponse,
  ParseImageResponse,
  ParseTextResponse,
  SaveTrainingLine,
} from '@mrrmlab/api-client';
import type {
  AddShoppingListItemDto,
  CreateShoppingListDto,
  ShoppingList,
  ShoppingListItem,
  ShoppingListWithItems,
  TransferMissingShoppingItemsDto,
  UpdateShoppingListDto,
  UpdateShoppingListItemDto,
} from './types';
import {
  toCreateListItemDto,
  toShoppingListItem,
  toShoppingListWithItems,
  toShoppingList,
  toUpdateListItemDto,
} from './adapters';
import { useApiClient } from './api-client-context';
import { shoppingListKeys } from './query-keys';

type ShoppingItemData = Extract<ListItem['data'], { type: 'shopping' }>;

function assertShoppingItem(item: ListItem): ShoppingListItem {
  const mapped = toShoppingListItem(item);
  if (!mapped) {
    throw new Error(
      `[feature-shopping-list] expected shopping item, got data.type=${item.data.type}`,
    );
  }
  return mapped;
}

function extractShoppingData(item: ShoppingListItem): ShoppingItemData {
  return {
    type: 'shopping',
    title: item.title,
    checked: item.checked,
    missing: item.missing,
    quantity: item.quantity,
    ...(item.description !== undefined ? { description: item.description } : {}),
  };
}

export function useShoppingLists(
  options?: Omit<UseQueryOptions<ShoppingList[]>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<ShoppingList[]>({
    queryKey: shoppingListKeys.lists(),
    queryFn: async ({ signal }) => {
      const lists = await client.lists.list(signal);
      return lists
        .map(toShoppingList)
        .filter((x): x is ShoppingList => x !== null);
    },
    ...options,
  });
}

export function useShoppingList(
  id: string,
  options?: Omit<UseQueryOptions<ShoppingListWithItems>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<ShoppingListWithItems>({
    queryKey: shoppingListKeys.detail(id),
    queryFn: async ({ signal }) => {
      const list = await client.lists.get(id, signal);
      const mapped = toShoppingListWithItems(list);
      if (!mapped) {
        throw new Error(
          `[feature-shopping-list] list ${id} has type '${list.type}', expected 'shopping'`,
        );
      }
      return mapped;
    },
    ...options,
  });
}

type MutationExtras<TData, TVars> = Omit<
  UseMutationOptions<TData, Error, TVars>,
  'mutationFn'
>;

export function useCreateShoppingList(
  options?: MutationExtras<ShoppingList, CreateShoppingListDto>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<ShoppingList, Error, CreateShoppingListDto>({
    mutationFn: async (dto) => {
      const created = await client.lists.create({
        type: 'shopping',
        title: dto.title,
        ...(dto.plannedDate !== undefined ? { plannedDate: dto.plannedDate } : {}),
      });
      const mapped = toShoppingList(created);
      if (!mapped) {
        throw new Error('[feature-shopping-list] server returned non-shopping list on create');
      }
      return mapped;
    },
    ...options,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: shoppingListKeys.lists() });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useUpdateShoppingList(
  options?: MutationExtras<ShoppingList, { id: string; dto: UpdateShoppingListDto }>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<ShoppingList, Error, { id: string; dto: UpdateShoppingListDto }>({
    mutationFn: async ({ id, dto }) => {
      const updated = await client.lists.update(id, dto);
      const mapped = toShoppingList(updated);
      if (!mapped) {
        throw new Error('[feature-shopping-list] server returned non-shopping list on update');
      }
      return mapped;
    },
    ...options,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: shoppingListKeys.lists() });
      qc.invalidateQueries({ queryKey: shoppingListKeys.detail(vars.id) });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useDeleteShoppingList(options?: MutationExtras<void, string>) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => client.lists.delete(id),
    ...options,
    onSuccess: (data, id, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: shoppingListKeys.lists() });
      qc.removeQueries({ queryKey: shoppingListKeys.detail(id) });
      options?.onSuccess?.(data, id, onMutateResult, ctx);
    },
  });
}

export function useAddShoppingListItem(
  listId: string,
  options?: MutationExtras<ShoppingListItem, AddShoppingListItemDto>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<ShoppingListItem, Error, AddShoppingListItemDto>({
    mutationFn: async (dto) => {
      const created = await client.lists.items.add(listId, toCreateListItemDto(dto));
      return assertShoppingItem(created);
    },
    ...options,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: shoppingListKeys.detail(listId) });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useUpdateShoppingListItem(
  listId: string,
  options?: MutationExtras<
    ShoppingListItem,
    { itemId: string; dto: UpdateShoppingListItemDto }
  >,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    ShoppingListItem,
    Error,
    { itemId: string; dto: UpdateShoppingListItemDto }
  >({
    mutationFn: async ({ itemId, dto }) => {
      const cached = qc.getQueryData<ShoppingListWithItems>(shoppingListKeys.detail(listId));
      const currentItem = cached?.items.find((i) => i.id === itemId);
      if (!currentItem) {
        // Falling back to the generic endpoint without merge would truncate
        // fields — refuse loudly instead of silently losing data.
        throw new Error(
          `[feature-shopping-list] cannot update item ${itemId}: current data not in cache`,
        );
      }
      const updated = await client.lists.items.update(
        listId,
        itemId,
        toUpdateListItemDto(dto, extractShoppingData(currentItem)),
      );
      return assertShoppingItem(updated);
    },
    ...options,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: shoppingListKeys.detail(listId) });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useDeleteShoppingListItem(
  listId: string,
  options?: MutationExtras<void, string>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (itemId) => client.lists.items.delete(listId, itemId),
    ...options,
    onSuccess: (data, itemId, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: shoppingListKeys.detail(listId) });
      options?.onSuccess?.(data, itemId, onMutateResult, ctx);
    },
  });
}

export function useTransferMissingShoppingItems(
  listId: string,
  options?: MutationExtras<ShoppingListWithItems, TransferMissingShoppingItemsDto>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<ShoppingListWithItems, Error, TransferMissingShoppingItemsDto>({
    mutationFn: async (dto) => {
      const result = await client.lists.items.transferMissing(listId, dto);
      const mapped = toShoppingListWithItems(result);
      if (!mapped) {
        throw new Error('[feature-shopping-list] transfer-missing did not return a shopping list');
      }
      return mapped;
    },
    ...options,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: shoppingListKeys.lists() });
      qc.invalidateQueries({ queryKey: shoppingListKeys.detail(data.id) });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

type ImageFile = Blob | { uri: string; name: string; type: string };

export function useParseShoppingListImage(
  options?: MutationExtras<ParseImageResponse, { file: ImageFile; model?: string }>,
) {
  const client = useApiClient();
  return useMutation<ParseImageResponse, Error, { file: ImageFile; model?: string }>({
    mutationFn: ({ file, model }) => client.lists.parseImage(file, { model }),
    ...options,
  });
}

export function useParseShoppingListText(
  options?: MutationExtras<ParseTextResponse, { text: string }>,
) {
  const client = useApiClient();
  return useMutation<ParseTextResponse, Error, { text: string }>({
    mutationFn: ({ text }) => client.lists.parseText(text),
    ...options,
  });
}

export function useDebugParseShoppingListImage(
  options?: MutationExtras<DebugParseImageResponse, { file: ImageFile; model?: string }>,
) {
  const client = useApiClient();
  return useMutation<DebugParseImageResponse, Error, { file: ImageFile; model?: string }>({
    mutationFn: ({ file, model }) => client.lists.debugParseImage(file, { model }),
    ...options,
  });
}

export function useSaveTraining(
  options?: MutationExtras<void, { lines: SaveTrainingLine[]; model?: string }>,
) {
  const client = useApiClient();
  return useMutation<void, Error, { lines: SaveTrainingLine[]; model?: string }>({
    mutationFn: ({ lines, model }) => client.lists.saveTraining(lines, { model }),
    ...options,
  });
}
